<template>
  <div>
    <b-button-group size="sm">
      <b-button
        v-b-tooltip.hover
        variant="outline-info cursor-pointer"
        title="Start video recording"
        @click="startRecording"
      >
        <b-icon icon="camera-reels" />
      </b-button>
      <b-button
        v-b-tooltip.hover
        variant="outline-info cursor-pointer"
        title="Start video and screen recording"
        @click="startVideoAndScreenRecording"
      >
        <b-icon icon="fullscreen" />
      </b-button>
      <b-button
        v-b-tooltip.hover
        variant="outline-info cursor-pointer"
        title="Start screen recording with audio only"
        @click="startScreenRecording"
      >
        <b-icon icon="display" />
      </b-button>
      <b-button
        v-if="recording"
        v-b-tooltip.hover
        variant="outline-danger cursor-pointer"
        title="Stop recording"
        @click="stopRecording"
      >
        <b-icon icon="stop-circle" />
      </b-button>
      <b-button
        v-if="recordingUploading"
        v-b-tooltip.hover
        variant="outline-info cursor-pointer"
        title="Uploading..."
      >
        <b-spinner
          small
          label="Spinning"
        />
      </b-button>
    </b-button-group>
    
    <b-button
      v-b-tooltip.hover
      variant="outline-info cursor-pointer"
      title="Show recordings"
      size="sm"
      @click="handleShowRecordingsBtn"
    >
      <b-spinner
        v-if="recordingsLoading"
        small
        label="Spinning"
      />
      <b-icon 
        v-else 
        icon="folder" 
      />
    </b-button>
    
    <div 
      v-show="recording && showPreview"
      class="video-preview mt-3"
    >
      <video
        ref="videoPreview"
        autoplay
        playsinline
        muted
      />
    </div>

    <canvas
      :id="canvasId"
      style="display:none;"
    />
    
    <RecordingsListModal 
      :recordings="recordings" 
      :current-page="currentPage"
      :total="recordingsCount"
      :uploaded-video-file="uploadedVideoFileId"
      @pageChanged="handlePageChanged"
      @delete="handleDeletedBtn"
      @close="handleRecordingsModalClose"
      @changeName="handleChangeName"
    />
  </div>
</template>

<script>
import { createNamespacedHelpers } from 'vuex'
const { mapActions } = createNamespacedHelpers('fileBrowser')

import RecordingsListModal from "@frontend/components/modules/video-screen-recording/recordings-list-modal.vue";
import FileBrowser from "@frontend/services/api/file-browser";

export default {
  name: 'VideoScreenRecording',
  components: {RecordingsListModal},
  props: {
    canvasId: {
      type: String,
      default: 'recordScreenAndVideoCanvas'
    },
    showPreview: {
      type: Boolean,
      default: true
    }
  },
  data: () => ({
    webcamStream: null,
    screenStream: null,
    canvasStream: null,
    mediaRecorder: null,
    chunks: [],
    recordedVideoFile: null,
    recording: false,
    recordings: [],
    recordingsCount: null,
    currentPage: 1,
    recordingsLoading: false,
    recordingUploading: false,
    screenRecordingFolderId: null,
    uploadedVideoFileId: null,
    mimeType: 'video/mp4; codecs="avc1.42E01E, mp4a.40.2"',
  }),
  
  async mounted() {
    const { data: folder } = await FileBrowser.getFolderByName('Screen recording');

    this.screenRecordingFolderId = folder[0]?.id;
  },
  
  methods: {
    ...mapActions([
      'uploadScreenRecording'
    ]),
    async startRecording() {
      try {
        this.recordedVideoUrl = '';
        
        this.webcamStream = await navigator.mediaDevices.getUserMedia({
          video: true,
          audio: true
        });

        this.$refs.videoPreview.srcObject = this.webcamStream;

        if (!MediaRecorder.isTypeSupported(this.mimeType)) {
          toastr.error('Your browser does not support the required video format for recording. use Google Chrome');
          return;
        }
        this.mediaRecorder = new MediaRecorder(this.webcamStream, {
          mimeType: this.mimeType
        });
        
        this.chunks = [];

        this.mediaRecorder.ondataavailable = (e) => {
          if (e.data.size > 0) {
            this.chunks.push(e.data);
          }
        }

        this.mediaRecorder.onstop = () => {
          const blob = new Blob(this.chunks, { type: 'video/mp4' });

          this.recordedVideoFile = new File([blob], 'recording.mp4', { type: 'video/mp4' });
          
          this.uploadVideo();
        }

        this.mediaRecorder.start();

        this.recording = true;

      } catch (error) {
        console.error('Failed to start recording:', error);
      }
    },

    async startVideoAndScreenRecording() {
      this.chunks = [];
      
      this.screenStream = await navigator.mediaDevices.getDisplayMedia({ video: true });
      this.webcamStream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });

      const videoScreen = document.createElement('video');
      videoScreen.srcObject = this.screenStream;
      videoScreen.muted = true;
      await videoScreen.play();

      const videoCam = document.createElement('video');
      videoCam.srcObject = this.webcamStream;
      videoCam.muted = true;
      await videoCam.play();

      const canvas = document.getElementById('recordScreenAndVideoCanvas');
      const ctx = canvas.getContext('2d');

      canvas.width = 1280;
      canvas.height = 720;

      function drawFrame() {
        ctx.drawImage(videoScreen, 0, 0, canvas.width, canvas.height);
        ctx.drawImage(videoCam, canvas.width - 320, canvas.height - 240, 300, 225);
        requestAnimationFrame(drawFrame);
      }

      drawFrame();

      this.canvasStream = canvas.captureStream(30);
      
      const webcamAudioTrack = this.webcamStream.getAudioTracks()[0];
      
      if (webcamAudioTrack) {
        this.canvasStream.addTrack(webcamAudioTrack);
      }

      this.mediaRecorder = new MediaRecorder(this.canvasStream, {
        mimeType: 'video/mp4; codecs="avc3.42E01E, mp4a.40.2"'
      });

      this.mediaRecorder.onstart = () => {
        this.recording = true;

        this.$refs.videoPreview.srcObject = this.canvasStream;
      }

      this.mediaRecorder.ondataavailable = e => this.chunks.push(e.data);

      this.mediaRecorder.onstop = () => {
        const blob = new Blob(this.chunks, { type: 'video/mp4' });
        
        this.recordedVideoFile = new File([blob], 'recording.mp4', { type: 'video/mp4' });
        
        this.uploadVideo();
      }

      this.mediaRecorder.start();
    },

    async startScreenRecording() {
      try {
        this.chunks = [];

        this.screenStream = await navigator.mediaDevices.getDisplayMedia({ video: true });
        this.webcamStream = await navigator.mediaDevices.getUserMedia({ audio: true });

        const webcamAudioTrack = this.webcamStream.getAudioTracks()[0];

        if (webcamAudioTrack) {
          this.screenStream.addTrack(webcamAudioTrack);
        }

        this.mediaRecorder = new MediaRecorder(this.screenStream, {
          mimeType: 'video/mp4; codecs="avc3.42E01E, mp4a.40.2"'
        });

        this.$refs.videoPreview.srcObject = this.screenStream;

        this.mediaRecorder.ondataavailable = (e) => {
          if (e.data.size > 0) {
            this.chunks.push(e.data);
          }
        }

        this.mediaRecorder.onstop = () => {
          const blob = new Blob(this.chunks, { type: 'video/mp4' });

          this.recordedVideoFile = new File([blob], 'recording.mp4', { type: 'video/mp4' });

          this.uploadVideo();
        }

        this.mediaRecorder.start();

        this.recording = true;
      } catch (error) {
        console.error('Failed to start recording:', error);
      }
    },

    stopRecording() {
      this.mediaRecorder.stop();
      this.webcamStream.getTracks().forEach((track) => track.stop());
      
      if (this.screenStream) {
        this.screenStream.getTracks().forEach((track) => track.stop());
      }
      
      this.recording = false;

      this.webcamStream = null;
      this.screenStream = null;
      this.mediaRecorder = null;
      this.canvasStream = null;
    },

    async uploadVideo() {
      try {
        this.recordingUploading = true;

        const { folderId, fileId } = await this.uploadScreenRecording({ file: this.recordedVideoFile });

        this.screenRecordingFolderId = folderId;
        this.uploadedVideoFileId = fileId;
        
        this.recordedVideoFile = null;
        this.recordingUploading = false;

        this.handleShowRecordingsBtn();
      } catch (err) {
        console.log(err);
        toastr.error('Failed to upload the recording');
      }
    },
    
    async handleShowRecordingsBtn() {
      this.recordingsLoading = true;
      
      await this.fetchRecordings();

      this.$bvModal.show('recordingsListModal');

      this.recordingsLoading = false;
    },

    async fetchRecordings(page = 1) {
      try {
        if (!this.screenRecordingFolderId) return;
        
        const { data } = await FileBrowser.getFolderContent(this.screenRecordingFolderId, page);

        this.recordings = data.items.filter(item => item.type !== 'folder');
        this.recordingsCount = data.totalCount;

      } catch (err) {
        console.log(err);
      }
    },
    
    async handlePageChanged(page) {
      await this.fetchRecordings(page);
    },
    
    async handleDeletedBtn(id) {
      try {
        await FileBrowser.deleteFolderFile(id);
        
        this.recordings = this.recordings.filter(rec => rec.id !== id);
        
        toastr.success('Video deleted');
      } catch (err) {
        console.log(err);
      }
    },
    
    handleRecordingsModalClose() {
      this.uploadedVideoFileId = null;
    },

    async handleChangeName(data) {
      try {
        await FileBrowser.updateFolderFile(data);

        this.recordings = this.recordings.map(item => {
          if (item.id === data.id) {
            return {
              ...item,
              name: data.name
            }
          }
          
          return item;
        })
      } catch (e) {
        console.log(e);
        toastr.error('Failed to rename video');
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.video-preview {
  position: relative;
  width: 100%;
  padding-top: 200px;
  
  video {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }
}

</style>
